import {useCallback, useState} from "react";
import classNames from "classnames/bind";
import {Dayjs} from "dayjs";

import Button from "~/components/button";
import {CnInput} from "~/components/cn-input";
import {CnRangePicker} from "~/components/cn-range-picker";
import {CnSelect} from "~/components/cn-select";
import {CustomLabelTable} from "~/components/custom-label-table/custom-label-table";
import {FilterArea} from "~/components/filter-area";
import {PageTitle} from "~/components/page-title";
import {IncidentBoardListType} from "~/data/board/incident/use-incident.interface";
import {useIncidentBoard} from "~/data/board/incident/use-incident-board";
import {useUser} from "~/data/user";
import {IncidentBoardModal} from "~/pages/board/incident/modal/incident-board.modal";

import styles from "./incident-board.page.module.scss";

const cx = classNames.bind(styles);

const IncidentBoardPage = () => {
    const [modal, setModal] = useState<boolean>(false);
    const [modalData, setModalData] = useState<IncidentBoardListType | null>(null);
    const [filter, setFilter] = useState<{
        title: string;
        dateRange: [Dayjs, Dayjs] | null;
        useYn: "Y" | "N" | null;
    }>({
        title: "",
        dateRange: null,
        useYn: null,
    });

    const {user} = useUser();
    const {list, submit, loading} = useIncidentBoard({
        title: filter.title || null,
        searchStartDate: filter.dateRange ? filter.dateRange[0].format("YYYY-MM-DD") : null,
        searchEndDate: filter.dateRange ? filter.dateRange[1].format("YYYY-MM-DD") : null,
        useYn: filter.useYn,
    });

    const handleReset = useCallback(() => {
        setFilter({
            title: "",
            dateRange: null,
            useYn: null,
        });
    }, []);
    const handleInquiry = () => {
        submit();
    };

    return (
        <div>
            {modal && (
                <IncidentBoardModal
                    data={modalData}
                    close={() => {
                        setModal(false);
                        setModalData(null);
                    }}
                    refresh={() => submit()}
                />
            )}
            <PageTitle>사고사례 게시판</PageTitle>
            <FilterArea>
                <div className={cx("filter-inner")}>
                    <CnInput
                        label={"제목"}
                        placeholder={"제목 검색"}
                        value={filter.title}
                        onChange={(e) => setFilter((p) => ({...p, title: e.target.value}))}
                    />
                    <CnRangePicker
                        label={"기간"}
                        value={filter.dateRange}
                        onChange={(e) => setFilter((p) => ({...p, dateRange: e}))}
                    />
                    <CnSelect
                        label={"사용여부"}
                        value={filter.useYn || ""}
                        onChange={(e) => setFilter((p) => ({...p, useYn: e || null}))}
                        options={[
                            {
                                label: "전체",
                                value: "",
                            },
                            {
                                label: "사용",
                                value: "Y",
                            },
                            {
                                label: "미사용",
                                value: "N",
                            },
                        ]}
                    />
                    <div className={cx("button-container")}>
                        <Button label={"조회"} color={"primary"} onClick={handleInquiry} />
                        <Button label={"검색조건 초기화"} onClick={handleReset} />
                    </div>
                </div>
            </FilterArea>
            <div className={cx("table-container")}>
                <CustomLabelTable
                    label={
                        <>
                            <span>{list?.allRecordCnt ?? 0} 건</span>
                            {user?.division === "MEMBER" && (
                                <Button label={"등록하기"} onClick={() => setModal(true)} />
                            )}
                        </>
                    }
                    totalCount={list?.allRecordCnt ?? 0}
                    perPage={100}
                    pagination={false}
                    rowKey={"id"}
                    onRow={(record) => ({
                        onClick: () => {
                            setModal(true);
                            setModalData(record);
                        },
                    })}
                    noPagination
                    page={1}
                    dataSource={list?.list ?? []}
                    onPageChange={(e) => console.log(e)}
                    columns={[
                        {
                            title: "No",
                            width: "5%",
                            align: "center",
                            render: (_, __, idx) => idx + 1,
                        },
                        {
                            title: "제목",
                            dataIndex: "title",
                            align: "center",
                            width: "60%",
                        },
                        {
                            title: "기간",
                            dataIndex: "startDate",
                            align: "center",
                            render: (value, record) => value + " ~ " + record.endDate,
                        },
                        {
                            title: "사용여부",
                            dataIndex: "useYn",
                            align: "center",
                            render: (value) => (value === "Y" ? "사용" : "미사용"),
                        },
                    ]}
                />
            </div>
        </div>
    );
};

export {IncidentBoardPage};
